<script setup lang="ts">
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const deviceCheckoutUrl = "https://buy.stripe.com/8x27sK46oehM3Wj6mA4Ja02";

const specKeys = ["screen", "wifi", "os"] as const;
</script>

<template>
  <section id="device" class="bg-[var(--color-bg-primary)] px-5 py-12 md:px-12 md:py-24">
    <div class="mx-auto max-w-[var(--max-w-page)]">
      <div class="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-16">
        <!-- Device visual -->
        <div data-reveal class="fade-up order-2 md:order-1">
          <img
            src="/screenshots/device-coming-soon.webp"
            :alt="t('device.imageAlt')"
            class="aspect-[16/9] w-full rounded-3xl border border-[var(--color-border)] object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        <!-- Copy + CTA -->
        <div class="order-1 md:order-2">
          <p
            data-reveal
            class="fade-up inline-flex items-center gap-2 rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary-light)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-primary)]"
          >
            <span class="inline-block size-1.5 rounded-full bg-[var(--color-primary)]"></span>
            {{ t("device.label") }}
          </p>
          <h2
            data-reveal
            data-reveal-delay="1"
            class="fade-up mt-4 text-2xl font-semibold text-[var(--color-text-primary)] md:text-4xl"
            style="font-family: var(--font-display)"
          >
            {{ t("device.heading") }}
          </h2>
          <p
            data-reveal
            data-reveal-delay="2"
            class="fade-up mt-3 text-base text-[var(--color-text-secondary)] md:text-lg"
          >
            {{ t("device.subheading") }}
          </p>

          <!-- Specs -->
          <ul data-reveal data-reveal-delay="3" class="fade-up mt-6 flex flex-wrap gap-2">
            <li
              v-for="spec in specKeys"
              :key="spec"
              class="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-sm text-[var(--color-text-secondary)]"
            >
              {{ t(`device.specs.${spec}`) }}
            </li>
          </ul>

          <!-- Price block -->
          <div data-reveal data-reveal-delay="4" class="fade-up mt-8">
            <p
              class="text-4xl font-bold text-[var(--color-text-primary)]"
              style="font-family: var(--font-display)"
            >
              {{ t("device.price") }}
            </p>
            <p class="mt-1 text-sm text-[var(--color-text-muted)]">
              {{ t("device.priceNote") }}
            </p>
          </div>

          <!-- Disclosures -->
          <div
            data-reveal
            data-reveal-delay="5"
            class="fade-up mt-5 flex flex-col gap-2.5 text-sm text-[var(--color-text-secondary)]"
          >
            <div class="flex items-start gap-2">
              <UIcon
                name="i-lucide-truck"
                class="mt-0.5 size-4 shrink-0 text-[var(--color-primary)]"
              />
              <span>{{ t("device.shipping") }}</span>
            </div>
            <div class="flex items-start gap-2">
              <UIcon
                name="i-lucide-info"
                class="mt-0.5 size-4 shrink-0 text-[var(--color-primary)]"
              />
              <span>{{ t("device.customsNote") }}</span>
            </div>
            <div class="flex items-start gap-2">
              <UIcon
                name="i-lucide-package"
                class="mt-0.5 size-4 shrink-0 text-[var(--color-primary)]"
              />
              <span>{{ t("device.batch") }}</span>
            </div>
            <div class="flex items-start gap-2">
              <UIcon
                name="i-lucide-key"
                class="mt-0.5 size-4 shrink-0 text-[var(--color-primary)]"
              />
              <span>{{ t("device.subscriptionRequired") }}</span>
            </div>
          </div>

          <!-- CTA -->
          <a
            data-reveal
            data-reveal-delay="6"
            :href="deviceCheckoutUrl"
            class="fade-up mt-8 inline-block w-full rounded-full bg-[var(--color-primary)] px-8 py-3.5 text-center text-base font-semibold text-white transition-colors hover:bg-[var(--color-primary-hover)] md:w-auto"
          >
            {{ t("device.cta") }}
          </a>

          <!-- Terms note -->
          <p
            data-reveal
            data-reveal-delay="7"
            class="fade-up mt-4 text-xs text-[var(--color-text-muted)]"
          >
            <i18n-t keypath="device.termsNote" tag="span">
              <template #termsLink>
                <router-link
                  to="/terms-of-sale"
                  class="underline hover:text-[var(--color-text-secondary)]"
                >
                  {{ t("device.termsLinkLabel") }}
                </router-link>
              </template>
            </i18n-t>
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
